import { Container, Point } from "pixi.js";

export interface CameraState {
  /** World coords at the center of the screen. */
  cx: number;
  cy: number;
  /** Pixels per world-meter. >1 = zoomed in. */
  zoom: number;
}

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 20;

export function createCamera(): CameraState {
  return { cx: 0, cy: 0, zoom: 0.5 };
}

/** Screen pixel (relative to canvas) → world coords. */
export function screenToWorld(
  screenX: number,
  screenY: number,
  cam: CameraState,
  canvasW: number,
  canvasH: number,
): { x: number; y: number } {
  return {
    x: (screenX - canvasW / 2) / cam.zoom + cam.cx,
    y: (screenY - canvasH / 2) / cam.zoom + cam.cy,
  };
}

/** World coords → screen pixel (relative to canvas). */
export function worldToScreen(
  worldX: number,
  worldY: number,
  cam: CameraState,
  canvasW: number,
  canvasH: number,
): { x: number; y: number } {
  return {
    x: (worldX - cam.cx) * cam.zoom + canvasW / 2,
    y: (worldY - cam.cy) * cam.zoom + canvasH / 2,
  };
}

/**
 * Apply camera to a Pixi stage container.
 * We move the stage to the center of the canvas, then scale + translate
 * so that world point (cx, cy) lands at the canvas center.
 */
export function applyCamera(stage: Container, cam: CameraState, canvasW: number, canvasH: number): void {
  stage.scale.set(cam.zoom);
  stage.position.set(
    canvasW / 2 - cam.cx * cam.zoom,
    canvasH / 2 - cam.cy * cam.zoom,
  );
}

/** Zoom toward a screen-space point, keeping that world point under the cursor. */
export function zoomAt(
  cam: CameraState,
  screenX: number,
  screenY: number,
  canvasW: number,
  canvasH: number,
  factor: number,
): CameraState {
  const worldBefore = screenToWorld(screenX, screenY, cam, canvasW, canvasH);
  const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, cam.zoom * factor));

  // Adjust cx, cy so that the world point under the cursor stays fixed.
  const cx = worldBefore.x - (screenX - canvasW / 2) / newZoom;
  const cy = worldBefore.y - (screenY - canvasH / 2) / newZoom;

  return { cx, cy, zoom: newZoom };
}

/** Pan by a screen-space delta (pixels). */
export function panBy(
  cam: CameraState,
  dxPixels: number,
  dyPixels: number,
): CameraState {
  return {
    ...cam,
    cx: cam.cx - dxPixels / cam.zoom,
    cy: cam.cy - dyPixels / cam.zoom,
  };
}

/** Pixi Point helper for worldToScreen. */
export function worldPoint(x: number, y: number): Point {
  return new Point(x, y);
}